import { useEffect, useState } from 'react';
import { Divider, Flex, Select, Spin, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { PopupActionButtons } from './PopupActionButtons';
import { optionToLabelInValueFormat, defaultFilterOption } from './utils';

const { Text } = Typography;

// Type guard para verificar si la opción es de tipo DefaultOptionType
const isDefaultOptionType = (option) => {
	return !!option && typeof option === 'object' && ('label' in option || 'value' in option || 'children' in option);
};

// Ordena las opciones alfabéticamente en orden ascendente por defecto
const filterSortFn = (optionA, optionB) => {
	const orderA = optionA?.order ?? 0;
	const orderB = optionB?.order ?? 0;

	// Si las opciones tienen un orden distinto, se ordenan por ese orden
	if (orderA !== orderB) return orderB - orderA

	return String(optionA?.label ?? '').toLowerCase().localeCompare(String(optionB?.label ?? '').toLowerCase());
};

/**
 * Componente: Selector que utiliza el componente Select de Ant Design. Incluye la funcionalidad adicional para marcar/desmarcar
 * todos los ítems de la búsqueda actual cuando el modo es 'multiple' o 'tags'.
 * Se extienden las propiedades del componente [Select](https://ant.design/components/select#select-props) de Ant Design.
 *
 * @param mode Modo de selección: 'single' | 'multiple' | 'tags' (default: 'single').
 * @param femaleMode Indica si se deben usar términos de genero femenino en el componente (default: false).
 * @param parseNumericValue Indica si debe convertir el valor recibido ('value') a número si es un string numérico. No afecta las opciones ('options').
 *
 * @example
 * <SelectPlus mode='multiple' femaleMode /> // Utilización del componente en modo de selección múltiple con términos femeninos
 */
export const SelectPlus = ({
	mode = 'single',
	femaleMode = false,
	value,
	onChange,
	options = [],
	popupRender,
	loading = false,
	filterOption = true,
	labelInValue,
	onSearch,
	defaultActiveFirstOption,
	onGroup = false,
	popupMatchSelectWidth = false,
	parseNumericValue = false,
	onOpenChange,
	error = false,
	errorMessage,
	...selectProps
}) => {
	const [selectedValue, setSelectedValue] = useState();
	const [filteredOptions, setFilteredOptions] = useState(options);
	const [defaultOptionSet, setDefaultOptionSet] = useState(false); // Indica si la opción predeterminada ya ha sido establecida
	const [dropdownOpen, setDropdownOpen] = useState(false); // Indica si el dropdown está abierto

	const isSingleMode = mode === 'single';

	const handleChange = (newValue, newOption) => {
		setSelectedValue(newValue);
		onChange?.(newValue, newOption);
	};

	const handleSearch = (searchText) => {
		// Usa defaultFilterOption si filterOption es true, de lo contrario se filtra por filterOption siempre y cuando no sea false
		const currentFilterOption = filterOption === true ? defaultFilterOption : filterOption;
		if (currentFilterOption) {
			const currentFilteredOptions = options.filter(option => currentFilterOption(searchText, option)) || [];
			setFilteredOptions(currentFilteredOptions);
		}

		onSearch?.(searchText);
	};

	const handleOpenChange = (open) => {
		setDropdownOpen(open);
		onOpenChange?.(open);
	};

	const handleAction = (action) => {
		// Obtiene los valores de las opciones filtradas
		const filteredOptionsValues = filteredOptions.map(option => isDefaultOptionType(option) ? option.value : undefined);

		// Convierte selectedValue a un array
		const selectedValueArray = Array.isArray(selectedValue)
			? selectedValue
			: (selectedValue ? [selectedValue] : []);

		// Obtiene los valores actualmente seleccionados de los items de selectedValue dependiendo de si es de tipo DefaultOption o simple
		const currentSelectedValues = selectedValueArray.map(item => isDefaultOptionType(item) ? item.value : item);

		// Calcula los nuevos valores seleccionados en base a la acción realizada
		const newSelectedValues = (action === 'select'
			? [...new Set([...currentSelectedValues, ...filteredOptionsValues])]
			: currentSelectedValues.filter(item => !filteredOptionsValues.includes(item)) // 'deselect'
		).filter(item => item !== undefined && item !== null); // Elimina valores undefined y null para mantener solo las opciones válidas

		// Mapea los nuevos valores seleccionados a sus correspondientes opciones
		const newSelectedOptions = newSelectedValues.map(item => {
			const matchingOption = options.find(option => option.value === item);

			// Retorna la opción coincidente si se encuentra, de lo contrario, devuelve un objeto vacío, siguiendo el comportamiento estándar del componente Select de Ant Design
			// return matchingOption ? matchingOption : {} as OptionType;
			return matchingOption ? matchingOption : {};
		});

		setFilteredOptions(options); // Restablece 'filteredOptions' con todas las opciones

		// Si 'labelInValue' es true, se añade la propiedad 'key' a cada opción de newOption y se pasa como el primer parámetro a 'onChange'
		if (labelInValue) {
			const newSelectedValuesWithOptionDetails = newSelectedOptions.map((option, index) => {
				// Verifica si la opción está vacía, lo que indica que no se encontró una opción relacionada en options de los valores iniciales o establecidos manualmente
				const isOptionEmpty = Object.keys(option).length === 0;

				// Determina la opción final: si la option está vacía, obtiene la opción relacionada desde selectedValueArray, de lo contrario usa la opción actual
				const finalOption = isOptionEmpty
					? (selectedValueArray.find(item => isDefaultOptionType(item) ? item.value === newSelectedValues[index] : undefined) ?? { value: newSelectedValues[index] })
					: option;

				const optionWithLabelInValueFormat = optionToLabelInValueFormat(finalOption);

				return {
					...optionWithLabelInValueFormat,
					...(isOptionEmpty && { key: undefined }), // Deja key como undefined cuando no tiene una opción relacionada en options, siguiendo el comportamiento estándar del componente Select de Ant Design
				};
			});

			// handleChange(newSelectedValuesWithOptionDetails as ValueType, newSelectedOptions);
			handleChange(newSelectedValuesWithOptionDetails, newSelectedOptions);
		} else {
			// handleChange(newSelectedValues as ValueType, newSelectedOptions);
			handleChange(newSelectedValues, newSelectedOptions);
		}
	};

	const finalPopupRender = (menu) => {
		const internalPopupRender = (<>
			{!isSingleMode && <>
				<PopupActionButtons onAction={handleAction} femaleMode={femaleMode} />
				<Divider style={{ margin: 4 }} />
			</>}
			<Spin spinning={loading}>
				{menu}
			</Spin>
		</>);

		// Si hay un 'popupRender' personalizado, lo combina con el interno
		return popupRender ? popupRender(internalPopupRender) : internalPopupRender;
	};

	// Actualiza el estado interno 'selectedOption' cuando 'value' se modifica externamente
	useEffect(() => {
		if (value !== selectedValue) {
			const parsedValue = parseNumericValue && typeof value === 'string' && value.trim() && !Number.isNaN(Number(value))
				? Number(value)
				: value;

			setSelectedValue(parsedValue ?? undefined);
		}
	}, [value]);

	// Actualiza el estado de 'filteredOptions' para reflejar todas las opciones disponibles cuando la propiedad 'options' cambia
	useEffect(() => {
		setFilteredOptions(options);
	}, [options]);

	// Establece la primera opción disponible como predeterminada solo cuando 'defaultActiveFirstOption' es true y no se ha especificado un valor inicial
	useEffect(() => {
		if (!defaultActiveFirstOption || defaultOptionSet || loading || value !== undefined) return;

		// Se obtiene la primera opción disponible después de ordenarlas en base a 'filterSortFn'
		const [firstOption] = [...filteredOptions].sort(filterSortFn);

		if (isDefaultOptionType(firstOption)) {
			// Determina el valor de la opción por defecto dependiendo de 'isSingleMode' y el prop 'labelInValue'
			const defaultOptionValue = labelInValue ? optionToLabelInValueFormat(firstOption) : firstOption.value;
			const defaultSelectedValue = isSingleMode ? defaultOptionValue : [defaultOptionValue];
			const defaultSelectedOption = isSingleMode ? firstOption : [firstOption];

			// handleChange(defaultSelectedValue as ValueType, defaultSelectedOption);
			handleChange(defaultSelectedValue, defaultSelectedOption);
			setDefaultOptionSet(true); // Indica que la opción predeterminada ha sido establecida
		}
	}, [defaultActiveFirstOption, value, loading, filteredOptions]);

	return (
		<Flex vertical>
			<Select
				value={selectedValue}
				onChange={handleChange}
				onSearch={onGroup ? undefined : handleSearch}
				onOpenChange={handleOpenChange}
				mode={isSingleMode ? undefined : mode}
				options={filteredOptions}
				popupRender={finalPopupRender}
				popupMatchSelectWidth={popupMatchSelectWidth}
				placeholder='Seleccionar'
				maxTagCount={'responsive'} // Muestra un número con las opciones seleccionadas adicionales que ya no caben en el input
				suffixIcon={!dropdownOpen && loading // Muestra un spinner en el campo solo cuando el dropdown está cerrado y el Select está cargando
					? <Spin size='small' indicator={<LoadingOutlined />} />
					: undefined}
				showSearch
				optionFilterProp='label' // Busca por 'label' y no por 'value' que es el valor por defecto
				filterSort={filterSortFn}
				filterOption={onGroup} // Desactiva el filtrado interno del componente Select, ya que se maneja manualmente en handleSearch
				labelInValue={labelInValue}
				{...selectProps}
				status={error ? 'error' : selectProps.status}
				disabled={error || selectProps.disabled}
			/>
			{error && errorMessage && (
				<Text type='danger' ellipsis={{ tooltip: { color: 'red' } }} style={{ fontSize: '0.9em' }}>
					{errorMessage}
				</Text>
			)}
		</Flex>
	);
};